/**
 * 의약품 상세 화면.
 * GET 필드를 보여 주고, PATCH `DrugUpdateRequest`에 있는 항목만 수정한다.
 */
import { useDrugDetail, useUpdateDrug } from '@/entities/drug/hooks/useDrugs';
import { useUpdateDrugDataDrug } from '@/entities/drug-data/hooks/useDrugData';
import type { DrugDetailResponse } from '@/entities/drug/api/drug.api';
import {
  DrugAdditionalInfoModal,
  formatAdditionalSections,
  type DrugAdditionalField,
  type DrugAdditionalSection,
} from '@/pages/drugs/DrugAdditionalInfoModal';
import {
  ADMINISTRATION_ROUTE_TYPE_OPTIONS,
  BOOLEAN_FLAG_OPTIONS,
  DATA_SOURCE_TYPE_OPTIONS,
  ETC_OTC_TYPE_OPTIONS,
  EXPOSE_FLAG_OPTIONS,
  optionsWithCurrent,
  REGISTRATION_STATUS_TYPE_OPTIONS,
  REIMBURSEMENT_TYPE_OPTIONS,
  type DrugSelectOption,
} from '@/pages/drugs/drugFormOptions';
import { mapToDrugUpdateRequest } from '@/pages/drugs/drugUpdateUtils';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { DatePicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import { ToolbarRow } from '@/shared/components/ui/row';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useParams, useSearch } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const DisabledField: React.FC<{
  label: string;
  value?: string | number | boolean | null;
  multiline?: boolean;
}> = ({ label, value, multiline }) => {
  const display =
    value === true ? '예' : value === false ? '아니오' : (value ?? '-');

  return (
    <Field label={label} htmlFor={`disabled-${label}`}>
      {multiline ? (
        <Textarea
          id={`disabled-${label}`}
          disabled
          rows={3}
          className="bg-muted/30"
          value={String(display)}
        />
      ) : (
        <Input id={`disabled-${label}`} disabled value={String(display)} />
      )}
    </Field>
  );
};

const DetailSelect: React.FC<{
  label: string;
  value?: string;
  options: readonly DrugSelectOption[];
  disabled: boolean;
  placeholder?: string;
  onValueChange: (value: string) => void;
}> = ({ label, value, options, disabled, placeholder, onValueChange }) => {
  const items = optionsWithCurrent(options, value);
  return (
    <Field label={label}>
      <Select
        value={value || undefined}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={label}
          className={cn(disabled && 'bg-muted/30')}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {items.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};

const booleanSelectValue = (value: boolean | undefined) =>
  value === true ? 'true' : 'false';

export const DrugDetailPage: React.FC = () => {
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const { drugCode: drugCodeParam } = useParams({ strict: false }) as {
    drugCode?: string;
  };
  const search = useSearch({ strict: false }) as { refYm?: string };
  const refYm = search.refYm ?? '';

  const { data, isLoading, isError, error } = useDrugDetail(
    drugCodeParam && refYm ? { refYm, drugCode: drugCodeParam } : null,
  );
  const updateDrug = useUpdateDrug();
  const updateDrugDataDrug = useUpdateDrugDataDrug();

  const [edited, setEdited] = useState<Partial<DrugDetailResponse>>({});
  const [additionalSections, setAdditionalSections] = useState<
    Record<DrugAdditionalField, DrugAdditionalSection[]>
  >({
    direction: [],
    effect: [],
    precaution: [],
  });
  const [additionalModalField, setAdditionalModalField] =
    useState<DrugAdditionalField | null>(null);

  const form: Partial<DrugDetailResponse> = { ...data, ...edited };

  useEffect(() => {
    setEdited({});
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const canEdit = !readOnly;

  const patchForm = <K extends keyof DrugDetailResponse>(
    key: K,
    value: DrugDetailResponse[K],
  ) => {
    setEdited((prev) => ({ ...prev, [key]: value }));
  };

  const handleAdditionalSave = async (
    field: DrugAdditionalField,
    sections: DrugAdditionalSection[],
  ) => {
    if (!drugCodeParam || !refYm) return;
    try {
      await updateDrugDataDrug.mutateAsync({
        refYm,
        drugCode: drugCodeParam,
        body: {
          [field]: sections,
          registrationStatusType: form.registrationStatusType,
        },
      });
      setAdditionalSections((prev) => ({ ...prev, [field]: sections }));
      setEdited((prev) => ({
        ...prev,
        [field]: formatAdditionalSections(sections),
      }));
      toast.success('부가정보를 저장했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const AdditionalField = ({
    field,
    label,
    id,
  }: {
    field: DrugAdditionalField;
    label: string;
    id: string;
  }) => (
    <div className="space-y-1">
      <ToolbarRow justify="between" gap={8}>
        <Label htmlFor={id}>{label}</Label>
        <MenuWriteGate>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAdditionalModalField(field)}
          >
            <Pencil className="mr-1 h-3 w-3" />
            부가정보 편집
          </Button>
        </MenuWriteGate>
      </ToolbarRow>
      <Textarea
        id={id}
        rows={3}
        disabled
        className="bg-muted/30"
        value={String(form[field] ?? '')}
      />
    </div>
  );

  const handleSave = async () => {
    if (!drugCodeParam || !refYm) return;
    try {
      await updateDrug.mutateAsync({
        refYm,
        drugCode: drugCodeParam,
        data: mapToDrugUpdateRequest(form) as Record<string, unknown>,
      });
      toast.success('의약품 정보를 저장했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  if (!drugCodeParam || !refYm) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 의약품 경로입니다. 목록에서 다시 선택해 주세요.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="의약품 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        의약품 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToolbarRow justify="between">
        <ToolbarRow>
          <h2 className="text-2xl font-semibold">의약품 상세</h2>
        </ToolbarRow>
        <MenuWriteGate>
          <Button onClick={handleSave} disabled={updateDrug.isPending}>
            저장
          </Button>
        </MenuWriteGate>
      </ToolbarRow>

      <Card>
        <CardHeader>
          <CardTitle>{data.drugName}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <DisabledField label="기준년월" value={data.refYm} />
            <DisabledField label="의약품 코드" value={data.drugCode} />
            <DetailSelect
              label="노출여부"
              value={booleanSelectValue(form.exposeFlag)}
              options={EXPOSE_FLAG_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) =>
                patchForm('exposeFlag', value === 'true')
              }
            />
            <Field label="표준코드(MFDS)" htmlFor="drug-code-mfds">
              <Input
                id="drug-code-mfds"
                value={form.drugCodeMfds ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('drugCodeMfds', e.target.value)}
              />
            </Field>
            <Field label="제약사 코드" htmlFor="drug-code-pharma">
              <Input
                id="drug-code-pharma"
                value={form.drugCodePharma ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('drugCodePharma', e.target.value)}
              />
            </Field>
            <Field label="제약사명" htmlFor="pharma-company-name">
              <Input
                id="pharma-company-name"
                value={form.pharmaCompanyName ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('pharmaCompanyName', e.target.value)}
              />
            </Field>
            <Field label="의약품명" htmlFor="drug-name">
              <Input
                id="drug-name"
                value={form.drugName ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('drugName', e.target.value)}
              />
            </Field>
            <Field label="주성분 코드" htmlFor="main-ingredient-code">
              <Input
                id="main-ingredient-code"
                value={form.mainIngredientCode ?? ''}
                disabled={!canEdit}
                onChange={(e) =>
                  patchForm('mainIngredientCode', e.target.value)
                }
              />
            </Field>
            <Field label="심평원 가격" htmlFor="price-hira">
              <Input
                id="price-hira"
                type="number"
                value={form.priceHira ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('priceHira', Number(e.target.value))}
              />
            </Field>
            <Field label="제약사 가격" htmlFor="price-pharma">
              <Input
                id="price-pharma"
                type="number"
                value={form.pricePharma ?? ''}
                disabled={!canEdit}
                onChange={(e) =>
                  patchForm('pricePharma', Number(e.target.value))
                }
              />
            </Field>
            <Field label="단위수량" htmlFor="unit-quantity">
              <Input
                id="unit-quantity"
                value={form.unitQuantity ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('unitQuantity', e.target.value)}
              />
            </Field>
            <Field label="기준단위" htmlFor="base-unit">
              <Input
                id="base-unit"
                value={form.baseUnit ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('baseUnit', e.target.value)}
              />
            </Field>
            <DetailSelect
              label="급여 구분"
              value={form.reimbursementType}
              options={REIMBURSEMENT_TYPE_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) => patchForm('reimbursementType', value)}
            />
            <DetailSelect
              label="전문/일반"
              value={form.etcOtcType}
              options={ETC_OTC_TYPE_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) => patchForm('etcOtcType', value)}
            />
            <DetailSelect
              label="데이터 출처"
              value={form.dataSourceType}
              options={DATA_SOURCE_TYPE_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) => patchForm('dataSourceType', value)}
            />
            <DetailSelect
              label="투여경로"
              value={form.administrationRouteType}
              options={ADMINISTRATION_ROUTE_TYPE_OPTIONS}
              disabled={!canEdit}
              placeholder="내복 / 주사 / 외용 / 기타 / 경구"
              onValueChange={(value) =>
                patchForm('administrationRouteType', value)
              }
            />
            <Field label="ATC 코드" htmlFor="atc-code">
              <Input
                id="atc-code"
                value={form.atcCode ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchForm('atcCode', e.target.value)}
              />
            </Field>
            <DetailSelect
              label="등록 상태"
              value={form.registrationStatusType}
              options={REGISTRATION_STATUS_TYPE_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) =>
                patchForm('registrationStatusType', value)
              }
            />
            <DetailSelect
              label="품절 여부"
              value={booleanSelectValue(form.outOfStockFlag)}
              options={BOOLEAN_FLAG_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) =>
                patchForm('outOfStockFlag', value === 'true')
              }
            />
            <Field label="품절 시작일" htmlFor="oos-from">
              <DatePicker
                id="oos-from"
                value={form.outOfStockFrom?.slice(0, 10) ?? ''}
                disabled={!canEdit}
                onChange={(v) => patchForm('outOfStockFrom', v)}
              />
            </Field>
            <Field label="품절 종료일" htmlFor="oos-to">
              <DatePicker
                id="oos-to"
                value={form.outOfStockTo?.slice(0, 10) ?? ''}
                disabled={!canEdit}
                onChange={(v) => patchForm('outOfStockTo', v)}
              />
            </Field>
            <DetailSelect
              label="비교대조군"
              value={booleanSelectValue(form.comparatorFlag)}
              options={BOOLEAN_FLAG_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) =>
                patchForm('comparatorFlag', value === 'true')
              }
            />
            <DetailSelect
              label="생물학적동등성"
              value={booleanSelectValue(form.bioequivalenceFlag)}
              options={BOOLEAN_FLAG_OPTIONS}
              disabled={!canEdit}
              onValueChange={(value) =>
                patchForm('bioequivalenceFlag', value === 'true')
              }
            />
          </div>
          <div className="grid gap-4">
            <AdditionalField field="effect" label="효능·효과" id="effect" />
            <AdditionalField
              field="precaution"
              label="사용상 주의"
              id="precaution"
            />
            <AdditionalField field="direction" label="용법" id="direction" />
            <Field label="메모" htmlFor="memo">
              <Textarea
                id="memo"
                rows={3}
                disabled
                className="bg-muted/30"
                value={form.memo ?? ''}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {additionalModalField && (
        <DrugAdditionalInfoModal
          open={additionalModalField != null}
          field={additionalModalField}
          sections={additionalSections[additionalModalField]}
          onClose={() => setAdditionalModalField(null)}
          onSave={(sections) => {
            if (additionalModalField) {
              void handleAdditionalSave(additionalModalField, sections);
            }
          }}
        />
      )}
    </div>
  );
};
